import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent } from '@/components/ui/drawer'
import { useNavigate } from '@tanstack/react-router'
import { useQuickStartGuide } from './useQuickStartGuide'
import { QuickStartStepList, useStepSelection } from './QuickStartStepList'
import { QuickStartStepDrawers } from './QuickStartStepDrawers'
import { useCompleteOwnerOnboarding } from '@/lib/hooks/onboarding'
import type { QuickStartStep } from './QuickStartGuideDrawer'
import type { StepId } from './useQuickStartGuide'

interface AdminOnboardingQuickStartProps {
  steps: QuickStartStep[]
}

export function AdminOnboardingQuickStart({ steps }: AdminOnboardingQuickStartProps) {
  const filteredSteps = steps.filter((s) => s.id !== 'trip')
  const guide = useQuickStartGuide(filteredSteps)
  const navigate = useNavigate()
  const completeOwnerOnboarding = useCompleteOwnerOnboarding()
  const { effectiveSelectedId, setSelectedStepId } = useStepSelection(
    filteredSteps,
    guide.firstIncompleteId,
  )

  const handleContinue = () => {
    if (effectiveSelectedId) {
      guide.setActiveDrawer(effectiveSelectedId as StepId)
    }
  }

  const handleSkip = () => {
    completeOwnerOnboarding.mutate({})
    navigate({ to: '/operations' })
  }

  const handleFinish = () => {
    guide.handleFinish({ showDialog: false })
    navigate({ to: '/operations' })
  }

  if (guide.totalCount === 0) return null

  return (
    <>
      <Drawer open={true}>
        <DrawerContent className="bg-surface-L0">
          <div className="flex flex-col gap-6 items-center px-4 pt-6 pb-8 w-full">
            <p className="text-2xl text-content-brand-bold text-center font-primary">
              Get your operation ready.
            </p>

            <div className="w-full max-w-[361px]">
              <QuickStartStepList
                steps={filteredSteps}
                activeStepId={effectiveSelectedId}
                onStepClick={setSelectedStepId}
                onStepOpen={(id) => guide.setActiveDrawer(id as StepId)}
              />
            </div>

            <div className="flex items-center justify-center gap-4 text-xs">
              <span className="text-content-primary-subtle">
                {guide.completedCount} of {guide.totalCount} Complete
              </span>
              <span className="text-content-primary-subtle">|</span>
              <button
                type="button"
                onClick={handleSkip}
                className="text-content-brand-bold hover:underline"
              >
                Skip
              </button>
            </div>

            {guide.allCompleted ? (
              <Button
                onClick={handleFinish}
                variant="primary"
                roundness="round"
                className="w-full"
              >
                Finish
              </Button>
            ) : (
              <Button
                onClick={handleContinue}
                variant="primary"
                roundness="round"
                className="w-full"
              >
                Continue
              </Button>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <QuickStartStepDrawers
        activeDrawer={guide.activeDrawer}
        onDrawerClose={() => guide.handleDrawerClose()}
        showAllSetDialog={guide.showAllSetDialog}
        onAllSetClose={() => guide.setShowAllSetDialog(false)}
      />
    </>
  )
}
